import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import paymentService from '../../services/paymentService';
import analyticsService from '../../services/analyticsService';
import './PaymentResult.css';

const PaymentResult = () => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Normalizar status de ePayco al formato interno
  const normalizeStatus = (status) => {
    if (!status) return 'pending';
    const s = status.toLowerCase();
    if (s === 'aceptada' || s === 'approved' || s === 'completed') return 'approved';
    if (s === 'rechazada' || s === 'rejected') return 'rejected';
    if (s === 'pendiente' || s === 'pending') return 'pending';
    if (s === 'fallida' || s === 'failed') return 'failed';
    if (s === 'reversada' || s === 'reversed' || s === 'refunded') return 'rejected';
    return 'pending';
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refEpayco = urlParams.get('ref_epayco');
    const statusParam = urlParams.get('status');
    const messageParam = urlParams.get('message');

    // Si no hay referencia, mostrar resultado genérico con status de URL
    if (!refEpayco) {
      setPayment({
        ref_epayco: '',
        status: normalizeStatus(statusParam),
        invoice: '',
        amount: 0,
        currency: 'COP',
        description: messageParam || 'Tu pago está siendo procesado',
        payment_method: 'pse',
        created_at: new Date().toISOString(),
      });
      setLoading(false);
      return;
    }

    // Consultar la API real para obtener datos del pago
    const fetchPayment = async () => {
      try {
        const response = await paymentService.lookupPayment(refEpayco);
        response.status = normalizeStatus(response.status);
        setPayment(response);
        analyticsService.trackEvent('payment', 'result', response.status, response.payment_method, {
          amount: response.amount,
          ref_epayco: refEpayco,
        });
      } catch (err) {
        console.warn('Error fetching payment from API, using URL params:', err);
        // Fallback: usar los datos del query param si la API falla
        setPayment({
          ref_epayco: refEpayco,
          status: normalizeStatus(statusParam),
          invoice: '',
          amount: 0,
          currency: 'COP',
          description: 'Compra en Tablero Infinito',
          payment_method: 'pse',
          created_at: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPayment();

    // Auto-refresh: poll every 10s while payment is pending
    const interval = setInterval(async () => {
      try {
        const response = await paymentService.lookupPayment(refEpayco);
        response.status = normalizeStatus(response.status);
        setPayment(prev => {
          if (prev && prev.status === 'pending' && response.status !== 'pending') {
            return response;
          }
          if (prev && prev.status === 'pending') {
            return response;
          }
          return prev;
        });
        // Stop polling once we get a final status
        if (response.status !== 'pending' && normalizeStatus(response.status) !== 'pending') {
          clearInterval(interval);
        }
      } catch (pollErr) {
        // Silently ignore polling errors
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [location.search]);

  const getStatusIcon = status => {
    switch (status) {
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      case 'pending':
        return '⏳';
      case 'failed':
        return '💥';
      default:
        return '❓';
    }
  };

  // Fondo del círculo del icono de resultado. Tonos oscurecidos de los
  // colores de estado de la paleta, para que el icono blanco encima contraste.
  const getStatusColor = status => {
    switch (status) {
      case 'approved':
        return '#15803D';
      case 'rejected':
      case 'failed':
        return '#B91C1C';
      case 'pending':
        return '#B45309';
      default:
        return '#2A2A2F';
    }
  };

  const getStatusMessage = status => {
    switch (status) {
      case 'approved':
        return {
          title: '¡Pago Exitoso!',
          message: 'Tu pago ha sido procesado correctamente.',
          description: 'Recibirás una confirmación por email en breve.',
        };
      case 'rejected':
        return {
          title: 'Pago Rechazado',
          message: 'El pago fue rechazado por el banco.',
          description: 'Por favor, verifica los datos de tu tarjeta e intenta nuevamente.',
        };
      case 'pending':
        return {
          title: 'Pago Pendiente',
          message: 'Tu pago está siendo procesado.',
          description: 'Recibirás una notificación cuando el pago sea confirmado.',
        };
      case 'failed':
        return {
          title: 'Pago Fallido',
          message: 'Ocurrió un error al procesar el pago.',
          description: 'Por favor, intenta nuevamente o contacta al soporte.',
        };
      default:
        return {
          title: 'Estado Desconocido',
          message: 'El estado del pago no es claro.',
          description: 'Por favor, contacta al soporte para más información.',
        };
    }
  };

  const handleContinue = () => {
    navigate('/mis-pagos');
  };

  const handleTryAgain = () => {
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="payment-result-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Verificando el estado del pago...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result-container">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/mis-pagos')}>
            Ver Mis Pagos
          </button>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="payment-result-container">
        <div className="error">
          <h3>Pago no encontrado</h3>
          <p>No se pudo encontrar la información del pago.</p>
          <button className="btn btn-primary" onClick={() => navigate('/mis-pagos')}>
            Ver Mis Pagos
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusMessage(payment.status);

  return (
    <div className="payment-result-container">
      <div className="payment-result-card">
        <div className="result-header">
          <div className="result-icon" style={{ backgroundColor: getStatusColor(payment.status) }}>
            {getStatusIcon(payment.status)}
          </div>
          <h1 className="result-title">{statusInfo.title}</h1>
          <p className="result-message">{statusInfo.message}</p>
          <p className="result-description">{statusInfo.description}</p>
        </div>

        <div className="result-details">
          <h3>Detalles del Pago</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Referencia:</span>
              <span className="detail-value">{payment.ref_epayco}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Factura:</span>
              <span className="detail-value">{payment.invoice}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Monto:</span>
              <span className="detail-value">
                {paymentService.formatCurrency(payment.amount, payment.currency)}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Método de Pago:</span>
              <span className="detail-value">
                {paymentService.getPaymentMethodLabel(payment.payment_method)}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Descripción:</span>
              <span className="detail-value">{payment.description}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Fecha:</span>
              <span className="detail-value">
                {new Date(payment.created_at).toLocaleDateString('es-CO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
        </div>

        <div className="result-actions">
          {payment.status === 'approved' && (
            <button className="btn btn-primary" onClick={handleContinue}>
              Continuar
            </button>
          )}
          {(payment.status === 'rejected' || payment.status === 'failed') && (
            <button className="btn btn-primary" onClick={handleTryAgain}>
              Intentar Nuevamente
            </button>
          )}
          {payment.status === 'pending' && (
            <button className="btn btn-secondary" onClick={handleContinue}>
              Ver Mis Pagos
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            Volver al Inicio
          </button>
        </div>

        {payment.status === 'approved' && (
          <div className="success-notice">
            <h4>¿Qué sigue?</h4>
            <ul>
              <li>Recibirás un email de confirmación</li>
              <li>Puedes descargar tu recibo desde &quot;Mis Pagos&quot;</li>
              <li>Si tienes preguntas, contacta nuestro soporte</li>
            </ul>
          </div>
        )}

        {payment.status === 'pending' && (
          <div className="pending-notice">
            <h4>Información importante</h4>
            <p>
              Los pagos PSE pueden tardar hasta 15 minutos en procesarse. Los pagos en efectivo
              pueden tardar hasta 24 horas.
            </p>
          </div>
        )}

        {(payment.status === 'rejected' || payment.status === 'failed') && (
          <div className="error-notice">
            <h4>¿Necesitas ayuda?</h4>
            <p>
              Si el problema persiste, por favor contacta nuestro soporte con la referencia del
              pago: <strong>{payment.ref_epayco}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentResult;
